import {
    ActionMenu,
    Avatar,
    Colors,
    FOCUS_RING,
    formatDate,
    htmlToSlate,
    Icon,
    Pills,
    RichText,
    Text,
    Typography,
    type OptionProps,
    type PillsVariant,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { NoteComposer } from './NoteComposer';

import type { TranslationKey } from '@/i18n-key';
import type { User } from '@/types/directory/User';
import type { EmployeeNote, EmployeeNoteCategory, EmployeeNoteDraft } from '@/types/employee-note';

import { useAmelioTranslations, useNavigate, useSelectedAppLanguage } from '@/hooks';
import { meetingPlanPath } from '@/navigation/thematicMeetingsRoutes';
import { RouteName } from '@/navigation/types';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

/** Each folder wears its own library pill tone, so a mixed list reads at a glance. */
const CATEGORY_PILL_VARIANT: Record<EmployeeNoteCategory, PillsVariant> = {
    General: 'default',
    Performance: 'purple',
    Personal: 'primary',
    Collaboration: 'lightPurple',
    Career: 'violet',
};

interface NoteOrigin {
    labelKey: TranslationKey;
    /** Absent when there is no page left to link back to. */
    path?: string;
}

/**
 * Where a note was written, when it was written in a meeting or an assessment. A deleted meeting
 * keeps its notes, but its page is gone: the origin is named, never linked.
 */
const noteOriginOf = (note: EmployeeNote): NoteOrigin | null => {
    if (note.thematicMeetingId) {
        return note.isThematicMeetingDeleted
            ? { labelKey: 'performanceProfile.employeeNotes.fromDeletedMeeting' }
            : {
                  labelKey: 'performanceProfile.employeeNotes.fromMeeting',
                  path: meetingPlanPath(note.thematicMeetingId),
              };
    }
    if (note.cycleInstanceId) {
        return {
            labelKey: 'performanceProfile.employeeNotes.fromAssessment',
            path: `${RouteName.ManagerAssessmentResults}/${note.cycleInstanceId}`,
        };
    }
    return null;
};

interface NoteCardProps {
    note: EmployeeNote;
    /** Resolved from `note.authorId` by the list; `undefined` when the id is blank or unknown. */
    author?: User;
    /** Names the folder on the card: on for a mixed list, off when the list is one folder. */
    showCategory?: boolean;
    /**
     * `compact` opens on a quiet line of date and author; `identity` puts the author first, with
     * their avatar, and the date in a pill -- for a list read by who wrote what.
     */
    header?: 'compact' | 'identity';
    /** Shows the note without the menu that edits or deletes it, whatever the server allows. */
    readOnly?: boolean;
    /** Links back to the meeting or assessment the note came from; off where that is the page itself. */
    showOrigin?: boolean;
    /** The folders the editor offers; omit to keep the note's folder without asking. */
    selectableCategories?: readonly EmployeeNoteCategory[];
    editorPlaceholder: string;
    /** Resolves `true` once the note is saved; on `false` the editor stays open with the text. */
    onSave: (draft: EmployeeNoteDraft) => Promise<boolean>;
    onRequestDelete: () => void;
    isSaving?: boolean;
    dataTestId?: string;
}

/**
 * One employee note. A quiet header -- the date, then the author in small grey text, then the
 * menu that edits the note in place or deletes it, offering only what the server allows -- so the
 * note opens on its body, rendered read-only by `RichText`. Its folder, when asked, and the
 * meeting or assessment it came from close it: the folder is a theme its author picked, the link
 * is where the note was written.
 */
export const NoteCard = ({
    note,
    author,
    showCategory = false,
    header = 'compact',
    readOnly = false,
    showOrigin = true,
    selectableCategories,
    editorPlaceholder,
    onSave,
    onRequestDelete,
    isSaving = false,
    dataTestId = 'employee-note',
}: NoteCardProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);

    const authorName = author
        ? getFullName(author)
        : t('performanceProfile.employeeNotes.unknownAuthor');
    const body = useMemo(() => htmlToSlate(note.content), [note.content]);

    const canEdit = !readOnly && note.canEdit;
    const canDelete = !readOnly && note.canDelete;

    const actions: OptionProps[] = [
        ...(canEdit
            ? [
                  {
                      id: 'edit',
                      label: t('performanceProfile.employeeNotes.edit'),
                      onClick: () => setIsEditing(true),
                  },
              ]
            : []),
        ...(canDelete
            ? [
                  {
                      id: 'delete',
                      label: t('performanceProfile.employeeNotes.delete'),
                      danger: true,
                      onClick: onRequestDelete,
                  },
              ]
            : []),
    ];

    // Closing before the answer would throw the rewritten text away when the save fails.
    const handleSave = async (draft: EmployeeNoteDraft) => {
        if (await onSave(draft)) {
            setIsEditing(false);
        }
    };

    const origin = noteOriginOf(note);
    const originPath = origin?.path;

    const showFooter = !isEditing && (showCategory || (showOrigin && !!origin));

    const createdOn = (
        <time dateTime={note.createdAt}>{formatDate({ date: note.createdAt, language })}</time>
    );

    const menu = actions.length > 0 && (
        <ActionMenu
            items={actions}
            variant={'subtle'}
            size={'sm'}
            ariaLabel={t('performanceProfile.employeeNotes.actionsAria')}
            dataTestId={`${dataTestId}-actions`}
        />
    );

    return (
        <article
            className={'flex flex-col gap-3 rounded-xl border border-black/5 bg-white p-3 md:p-4'}
            data-testid={dataTestId}
        >
            {header === 'identity' ? (
                <header className={'flex items-start justify-between gap-3'}>
                    <div className={'flex min-w-0 items-center gap-3'}>
                        <Avatar
                            src={getProfilePictureUrl(author?.profilePictureUrl) || undefined}
                            alt={authorName}
                            initials={author ? getInitials(author) : ''}
                            bgColor={'gradient'}
                            size={'2xs'}
                        />

                        <Text
                            weight={'semibold'}
                            color={Colors.BASE_600}
                            truncate
                        >
                            {authorName}
                        </Text>
                    </div>

                    <div className={'flex shrink-0 items-center gap-2'}>
                        <Pills
                            variant={'violet'}
                            size={'sm'}
                        >
                            {createdOn}
                        </Pills>

                        {menu}
                    </div>
                </header>
            ) : (
                <header className={'flex items-center justify-between gap-2'}>
                    <p
                        className={
                            'text-growth-slate flex min-w-0 flex-wrap items-center gap-x-1.5 text-[12px] font-medium'
                        }
                    >
                        {createdOn}
                        <span aria-hidden={'true'}>·</span>
                        <span>{authorName}</span>
                    </p>

                    {menu}
                </header>
            )}

            {isEditing ? (
                <NoteComposer
                    initialContent={note.content}
                    category={note.category}
                    selectableCategories={selectableCategories}
                    placeholder={editorPlaceholder}
                    onSave={handleSave}
                    onCancel={() => setIsEditing(false)}
                    isSaving={isSaving}
                    dataTestId={`${dataTestId}-editor`}
                />
            ) : (
                <RichText
                    value={body}
                    readOnly
                    toolbar={false}
                    locale={language}
                    contentMinHeight={'auto'}
                    dataTestId={`${dataTestId}-body`}
                />
            )}

            {showFooter && (
                <footer className={'flex flex-wrap items-center gap-x-4 gap-y-2'}>
                    {showCategory && (
                        <Pills
                            variant={CATEGORY_PILL_VARIANT[note.category]}
                            size={'sm'}
                            dataTestId={`${dataTestId}-category`}
                        >
                            {t(`performanceProfile.employeeNotes.categories.${note.category}`)}
                        </Pills>
                    )}

                    {showOrigin &&
                        origin &&
                        (originPath ? (
                            <button
                                type={'button'}
                                onClick={() => navigate({ path: originPath })}
                                className={`${FOCUS_RING} text-primary inline-flex w-fit items-center gap-1.5 rounded-lg py-1 text-xs font-medium hover:underline`}
                                data-testid={`${dataTestId}-origin-link`}
                            >
                                <Icon
                                    name={'arrow-export-3'}
                                    size={'xs'}
                                    color={'inherit'}
                                    ariaHidden
                                />
                                {t(origin.labelKey)}
                            </button>
                        ) : (
                            <Typography
                                variant={'caption'}
                                color={Colors.BASE_600}
                            >
                                {t(origin.labelKey)}
                            </Typography>
                        ))}
                </footer>
            )}
        </article>
    );
};
